import React, { useState, useEffect } from 'react';
import Button from '@fenderdigital/ui/Button';
import Heading from '@fenderdigital/ui/Heading';
import Spacer from '@fenderdigital/ui/Spacer';
import { TriangleDown } from '@fenderdigital/ui/Icons';
import { useEscapeKey, useFocusTrap } from '@fenderdigital/custom-hooks';
import { CTACopy, AboutCopy, DismissCopy02 } from './AdConfig';
import { FreemiumAdProps } from './types';
import * as S from './FreemiumAd-styled';
import Link from '../Link';
import useWindowWidth from '../../hooks/useWindowWidth';

const freemiumBundleMoreInfoUrl =
  // eslint-disable-next-line max-len
  'https://www.fender.com/en-US/guitar-amplifiers/headphone-amplifiers/mustang-micro/2311300000.html';

const FreemiumBundleAd: React.FC<FreemiumAdProps> = ({ onDismiss, onUpgrade, ad }) => {
  const windowWidth = useWindowWidth();
  const [isMobile, setIsMobile] = useState<boolean | undefined>(undefined);
  const modalRef = useFocusTrap();
  const modalInnerRef = useEscapeKey<HTMLDivElement>(() => onDismiss());

  useEffect(() => {
    windowWidth && setIsMobile(windowWidth < 1280);
  }, [windowWidth]);

  return (
    <>
      <S.FreemiumBundleAdContainer
        data-id="freemium-ad"
        ref={modalRef}
        role="dialog"
        aria-modal
        aria-labelledby="bundle-heading"
      >
        <S.BundleAdImageContainer ref={modalInnerRef}>
          {isMobile ? (
            <S.BundleAdImage
              src={`${ad.mobile}.png`}
              srcSet={`${ad.mobile}@2x.png 2x`}
              alt={ad.alt}
            />
          ) : (
            <S.BundleAdImage
              src={`${ad.desktop}.jpg`}
              srcSet={`${ad.desktop}@2x.jpg 2x`}
              alt={ad.alt}
            />
          )}
          <S.BundleImageTextContainer>
            <Heading
              id="bundle-heading"
              as="h1"
              fontFamily="futuraDemi"
              textSize={[null, null, null, 'f7', 'f10']}
              textColor="white"
            >
              {ad.adCopy[0]}
            </Heading>
            <Spacer spacing={[null, null, null, 'sm2', 'md1']} />
            <S.SkewedBanner skewDirection="up">
              <Heading
                as="p"
                fontFamily="futuraDemi"
                textSize={[null, null, null, 'f3', 'f5']}
                textColor="white"
                textTransform="uppercase"
              >
                {ad.adCopy[1]}
              </Heading>
            </S.SkewedBanner>
            <Spacer spacing="lg1" />
            <Heading as="p" fontFamily="futuraDemi" textSize="f3" textColor="white">
              {ad.adCopy[2]}
            </Heading>
          </S.BundleImageTextContainer>
        </S.BundleAdImageContainer>
        <S.BundleBottomContainer>
          <S.BundleSecondBannerContainer>
            <S.SkewedBanner skewDirection="down">
              <Heading
                as="p"
                fontFamily="futuraDemi"
                textSize="f5"
                textColor="white"
                textTransform="uppercase"
              >
                {ad.adCopy[3]}
              </Heading>
            </S.SkewedBanner>
          </S.BundleSecondBannerContainer>
          <Spacer spacing={['md3', null, null, 'md4']} />
          <Button
            as={Link}
            to="/plans"
            shape="pill"
            isFluid
            dataId="freemium-bundle-ad-button"
            onClick={(): void => {
              onUpgrade();
            }}
          >
            {CTACopy}
          </Button>
          <Spacer spacing="md1" />
          <S.BundleSecondaryButtons>
            <Button
              size="small"
              shape="rectangle"
              colorScheme="white"
              textColor="black"
              hoverColor="white"
              data-id="freemium-bundle-ad-ignore-button"
              onClick={(): void => {
                onDismiss();
              }}
            >
              {DismissCopy02}
            </Button>
            <S.BundleExternalLink
              href={freemiumBundleMoreInfoUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-id="freemium-bundle-ad-external-link"
            >
              {AboutCopy}
              <TriangleDown fill="black" aria-hidden />
            </S.BundleExternalLink>
          </S.BundleSecondaryButtons>
          <Spacer spacing={['md2', null, null, 'md3']} />
        </S.BundleBottomContainer>
      </S.FreemiumBundleAdContainer>
      <S.FreemiumAdOverlay />
    </>
  );
};

export default FreemiumBundleAd;
